import { useMemo } from "react";
import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  BookOpen,
  Boxes,
  Cake,
  CircleHelp,
  ClipboardCheck,
  ClipboardList,
  FlaskConical,
  Gauge,
  GitPullRequest,
  GraduationCap,
  KeyRound,
  LayoutDashboard,
  Layers,
  Megaphone,
  MessagesSquare,
  PackageCheck,
  PanelTop,
  PenLine,
  Receipt,
  ScrollText,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Tags,
  Truck,
  UserRound,
  Users,
  Warehouse,
} from "lucide-react";

import { useAppSelector } from ".";
import { AppModule } from "../interfaces";
import { useGetMemberAccessQuery } from "@/redux/app/access.api";
import { get } from "lodash";
import { MemberAccess } from "@/interfaces/permission.interface";

export interface ISidebar {
  label: string;
  path: string;
  icon: LucideIcon;
  exact: boolean;
  access?: string[];
  hidden?: boolean;
  module?: AppModule;
  /** Nested nav (e.g. IMS → Inventory / Fulfilment / Pick Ups). */
  children?: ISidebar[];
}

const hiddenSidebar: ISidebar[] = [
  {
    label: "Change Password",
    path: "/change-password",
    icon: KeyRound,
    exact: true,
    hidden: true,
    module: AppModule.AUTH,
  },
  {
    label: "My Profile",
    path: "/profile",
    icon: UserRound,
    exact: true,
    hidden: true,
    module: AppModule.AUTH,
  },
];

export const sidebar: ISidebar[] = [
  {
    label: "Dashboard",
    path: "/dashboard",
    icon: LayoutDashboard,
    exact: true,
    module: AppModule.DASHBOARD,
  },
  {
    label: "Batches",
    path: "/batches",
    icon: Layers,
    exact: true,
    module: AppModule.BATCH,
  },
  {
    label: "Courses",
    path: "/courses",
    icon: BookOpen,
    exact: true,
    module: AppModule.COURSE,
  },
  {
    label: "Chats",
    path: "/chats",
    icon: MessagesSquare,
    exact: true,
    module: AppModule.CHAT,
  },
  {
    label: "Students",
    path: "/students",
    icon: GraduationCap,
    exact: true,
    module: AppModule.STUDENT,
  },
  {
    label: "Carts",
    path: "/carts",
    icon: ShoppingCart,
    exact: true,
    module: AppModule.PAYMENT,
  },
  {
    label: "Orders",
    path: "/orders",
    icon: Receipt,
    exact: true,
    module: AppModule.PAYMENT,
  },
  {
    label: "IMS",
    path: "/ims",
    icon: Boxes,
    exact: false,
    children: [
      {
        label: "Inventory",
        path: "/inventory",
        icon: Warehouse,
        exact: true,
        module: AppModule.INVENTORY,
      },
      {
        label: "Fulfilment",
        path: "/fulfilment",
        icon: PackageCheck,
        exact: false,
        module: AppModule.FULFILMENT,
      },
      {
        label: "Pick Ups",
        path: "/pickups",
        icon: Truck,
        exact: true,
        module: AppModule.FULFILMENT,
      },
    ],
  },
  {
    label: "Requests",
    path: "/requests",
    icon: GitPullRequest,
    exact: true,
    module: AppModule.REQUEST,
  },
  {
    label: "Birthdays",
    path: "/birthdays",
    icon: Cake,
    exact: true,
    module: AppModule.BIRTHDAY,
  },
  {
    label: "Members",
    path: "/members",
    icon: Users,
    exact: true,
    module: AppModule.MEMBER,
  },
  {
    label: "Access Control",
    path: "/permission",
    icon: ShieldCheck,
    exact: true,
    module: AppModule.PERMISSION,
  },
  {
    label: "Categories",
    path: "/categories",
    icon: Tags,
    exact: true,
    module: AppModule.CATEGORY,
  },
  {
    label: "Activity Logs",
    path: "/activities",
    icon: ScrollText,
    exact: true,
    module: AppModule.ACTIVITY,
  },
  {
    label: "Announcements",
    path: "/announcements",
    icon: Megaphone,
    exact: true,
    module: AppModule.ANNOUNCEMENT,
  },
  {
    label: "Attendance",
    path: "/attendance",
    icon: ClipboardCheck,
    exact: true,
    module: AppModule.ATTENDANCE,
  },
  {
    label: "Performance",
    path: "/performance",
    icon: Gauge,
    exact: false,
    module: AppModule.PERFORMANCE,
  },
  {
    label: "Questions",
    path: "/questions",
    icon: CircleHelp,
    exact: true,
    module: AppModule.QUESTION,
  },
  {
    label: "Tests",
    path: "/tests",
    icon: FlaskConical,
    exact: true,
    module: AppModule.TEST,
  },
  {
    label: "Assignments",
    path: "/assignments",
    icon: ClipboardList,
    exact: true,
    module: AppModule.TEST,
  },
  {
    label: "Articles",
    path: "/articles",
    icon: PenLine,
    exact: true,
    module: AppModule.ARTICLE,
  },
  {
    label: "Analytics",
    path: "/analytics",
    icon: BarChart3,
    exact: true,
    module: AppModule.ANALYTICS,
  },
  {
    label: "Cart Marketing",
    path: "/cart-marketing",
    icon: ShoppingBag,
    exact: true,
    module: AppModule.ANALYTICS,
  },
  {
    label: "Landing Edits",
    path: "/landing",
    icon: PanelTop,
    exact: true,
    module: AppModule.LANDING,
  },
  ...hiddenSidebar,
];

/** Leaf routes used for permission checks and page titles. */
export function flattenSidebarLeaves(items: ISidebar[]): ISidebar[] {
  const out: ISidebar[] = [];
  for (const item of items) {
    if (item.children?.length) {
      out.push(...flattenSidebarLeaves(item.children));
    } else {
      out.push(item);
    }
  }
  return out;
}

function canRead(
  item: ISidebar,
  permissionMap: Map<AppModule, MemberAccess>,
): boolean {
  if (!item.module) return true;
  const entry = permissionMap.get(item.module);
  if (entry === undefined) return true;
  return entry.read;
}

export default function useSidebar() {
  const { role } = useAppSelector((state) => state.auth.user);
  const { data } = useGetMemberAccessQuery({ role: role });

  const permissionMap = useMemo<Map<AppModule, MemberAccess>>(() => {
    if (!data) return new Map();
    return new Map(get(data, "entries").map((p) => [p.module, p]));
  }, [data]);

  return useMemo(() => {
    return sidebar
      .map((item) => {
        if (!item.children?.length) {
          return canRead(item, permissionMap) ? item : null;
        }
        const children = item.children.filter((c) => canRead(c, permissionMap));
        if (!children.length) return null;
        return { ...item, children };
      })
      .filter(Boolean) as ISidebar[];
  }, [permissionMap]);
}
